import { useMemo } from "react";

import {
  DEFAULT_CONTRACT_MULTIPLIER,
  LOCALE_OPTIONS,
} from "~/v2/constants/optionOrder";
import type { OptionSchema } from "../schema";

interface EstimatedCostParams {
  hasQuote: boolean;
  quantity: number;
  orderType: OptionSchema["orderType"];
  limitPrice: number | undefined;
  stopPrice: number | undefined;
  marketPrice: number;
  multiplier: string;
}

export const getEstimatedCost = ({
  hasQuote,
  quantity,
  orderType,
  limitPrice,
  stopPrice,
  marketPrice,
  multiplier,
}: EstimatedCostParams): string => {
  if (!hasQuote || !quantity) {
    return "-";
  }

  if ((orderType === "limit" || orderType === "stop_limit") && !limitPrice) {
    return "-";
  }

  if ((orderType === "stop" || orderType === "stop_limit") && !stopPrice) {
    return "-";
  }

  let price: number;
  switch (orderType) {
    case "market":
      price = marketPrice;
      break;
    case "stop":
      price = stopPrice ?? marketPrice;
      break;
    case "limit":
    case "stop_limit":
      price = limitPrice ?? marketPrice;
      break;
    default:
      price = marketPrice;
  }

  const cost =
    price * quantity * parseFloat(multiplier || DEFAULT_CONTRACT_MULTIPLIER);

  return Math.abs(cost).toLocaleString("en-US", LOCALE_OPTIONS);
};

export const useEstimatedCost = ({
  hasQuote,
  quantity,
  orderType,
  limitPrice,
  stopPrice,
  marketPrice,
  multiplier,
}: EstimatedCostParams) =>
  useMemo(
    () =>
      getEstimatedCost({
        hasQuote,
        quantity,
        orderType,
        limitPrice,
        stopPrice,
        marketPrice,
        multiplier,
      }),
    [
      hasQuote,
      quantity,
      orderType,
      limitPrice,
      stopPrice,
      marketPrice,
      multiplier,
    ],
  );
